import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSelectorListState } from 'helpers/hooks';
import { useGetManagerButtons } from '../utils/useGetManagerButtons';
import useManageManagerState from '../utils/useManageManagerState';
import { useGetRoleChangeButtons } from 'helpers/useGetRoleChangeButtons';
import { getAgencyUser } from 'redux/selectors';
import {
	PageLayout,
	FormSectionHeader,
	RoleChangeButton,
	ChangeRole,
} from 'components';
import RoleSelectorDisabled from '../../UtilityComponents/Form/RoleSelectorDisabled';
import ManagerBasicInfo from './ManagerBasicInfo';
import ManagerSocialWorkerList from './ManagerSocialWorkerList';
import ManagerBranchList from './ManagerBranchList';

export default function ManageManager() {
	const { managerId } = useParams();
	const userRole = useSelector((state) => state.user.user.role);
	const updatedRole = useSelector((state) => state.user.updateRole);
	const [disableRoleMessage, setDisableRoleMessage] = useState(null);
	const [updateRole, setUpdateRole] = useState(false);
	const socialWorker = useSelector(getAgencyUser(managerId));
	const {
		pageTitle,
		socialWorkerList,
		assignedSocialWorkerList,
		branchList,
		assignedBranch,
		role,
		setRole,
		loaded,
	} = useManageManagerState(socialWorker.id);

	const socialWorkerListState = useSelectorListState(
		socialWorkerList,
		assignedSocialWorkerList,
		{
			loaded,
		}
	);

	const branchListState = useSelectorListState(branchList, assignedBranch, {
		single: true,
		loaded,
	});

	const resetListChanges = () => {
		socialWorkerListState.resetChanges();
		branchListState.resetChanges();
	};

	const getDisableRole = () => {
		const { remove = [] } = socialWorkerListState.changes || {};

		// check if all assignedSWs are marked for removal
		const allRemoved = assignedSocialWorkerList?.length === remove.length;

		return !(!assignedSocialWorkerList.length > 0 || allRemoved)
			? 'Role can not be changed while Social Workers are assigned'
			: null;
	};

	const handleOnClick = () => {
		setUpdateRole(true);
	};

	useEffect(() => {
		setDisableRoleMessage(getDisableRole());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assignedSocialWorkerList.length, socialWorkerListState.changes?.remove]);

	const getBody = () => {
		const reqBody = { id: managerId };
		if (role !== 'manager') {
			reqBody.role = role;
		}
		const { add, remove } = branchListState?.changes || {};

		if (add.length > 0) {
			reqBody.branchId = add[0].id;
		} else if (remove.length > 0) {
			reqBody.branchId = '';
		}

		return {
			reqBody,
			socialWorkerChanges: socialWorkerListState.changes,
		};
	};

	const roleChangeButtons = useGetRoleChangeButtons(managerId, updatedRole);
	const managerButtons = useGetManagerButtons(getBody, resetListChanges);

	const buttonArray = updateRole ? roleChangeButtons : managerButtons;

	return (
		<PageLayout title={pageTitle} buttonArray={buttonArray} align='left'>
			{updateRole ? (
				<ChangeRole id={managerId} />
			) : (
				<>
					<ManagerBasicInfo />
					<ManagerBranchList selectorListState={branchListState} />
					<ManagerSocialWorkerList selectorListState={socialWorkerListState} />
					{userRole === 'admin' && (
						<>
							<FormSectionHeader hideTop>Management Options</FormSectionHeader>
							<RoleChangeButton handleOnClick={handleOnClick} />
						</>
					)}
				</>
			)}
		</PageLayout>
	);
}
